import React, { useId, useState } from "react";
import { change, dioptres, readAxis, readPower, scleralPower } from "./scleralPower";
import "./PowerCalculation.css";

/**
 * The lens power from the trial lens on the eye and the over-refraction
 * (see scleralPower.js). The trial lens is the one shown in the Suggested
 * Trial Lens card and the ordered base curve is Lens Specs'; only the
 * over-refraction and its vertex distance are typed in.
 */

const BLANK = { sphere: "", cyl: "", axis: "", vertex: "12" };

function readAll(f) {
  const errors = [];
  const get = (read) => {
    try { return read(); } catch (e) { errors.push(e.message); return undefined; }
  };
  const sphere = get(() => readPower(f.sphere, "sphere", 25));
  const cyl = get(() => readPower(f.cyl, "cylinder", 10));
  const axis = get(() => readAxis(f.axis));
  const vertexMm = get(() => {
    const s = String(f.vertex).trim();
    if (!s) return 12;
    const v = Number(s.replace(",", "."));
    if (!Number.isFinite(v) || v < 0 || v > 25) throw new Error("The vertex distance should be 0–25 mm.");
    return v;
  });
  if (cyl && axis === null) errors.push("A cylinder needs an axis.");
  const typed = [f.sphere, f.cyl, f.axis].some((x) => String(x).trim());
  const ok = typed && !errors.length;
  return { errors, overRefraction: ok ? { sphere: sphere || 0, cyl: cyl || 0, axis: axis || null } : null, vertexMm };
}

const axisLabel = (a) => (a == null ? "" : `${a}°`);

export default function PowerCalculation({ trial, order }) {
  const id = useId();
  const [f, setF] = useState(BLANK);
  const set = (key) => (e) => setF((x) => ({ ...x, [key]: e.target.value }));

  const head = (
    <>
      <h2 className="dash-card-title">Power Calculation</h2>
      <p className="dash-card-sub">The trial lens plus your over-refraction, vertexed, and compensated for the ordered base curve.</p>
    </>
  );
  if (!trial) {
    return (
      <div className="pc">
        {head}
        <p className="pc-empty">The trial lens comes from the Suggested Trial Lens card: open a scleral result that has one.</p>
      </div>
    );
  }

  const { errors, overRefraction, vertexMm } = readAll(f);
  const orderedBc = order && typeof order.bc === "number" ? order.bc : null;
  const out = overRefraction ? scleralPower({ overRefraction, vertexMm, trial, orderedBc }) : null;
  const box = (key, label, placeholder, extra = {}) => (
    <div className={`pc-box pc-box-${key}`}>
      <label htmlFor={`${id}${key}`}>{label}</label>
      <input id={`${id}${key}`} value={f[key]} onChange={set(key)} placeholder={placeholder}
             autoComplete="off" spellCheck={false} {...extra} />
    </div>
  );

  return (
    <div className="pc">
      {head}

      <dl className="pc-lenses">
        <dt>Trial lens</dt>
        <dd>{trial.diameter.toFixed(1)} {trial.code} · {dioptres(trial.power)} DS · BC {trial.bc.toFixed(2)}</dd>
        <dt>Ordered</dt>
        <dd>
          {order ? `${order.diameter.toFixed(1)} · ` : ""}
          {orderedBc != null ? `BC ${orderedBc.toFixed(2)}` : "no base curve in Lens Specs"}
          <span className="pc-from"> Lens Specs</span>
        </dd>
      </dl>
      <p className="pc-hint">Change the trial lens in its card and the diameter in Lens Specs; this follows both.</p>

      <fieldset className="pc-or">
        <legend>Over-refraction</legend>
        <div className="pc-boxes">
          {box("sphere", "Sphere", "PL")}
          {box("cyl", "Cylinder", "—")}
          {box("axis", "Axis", "—", { inputMode: "numeric" })}
          {box("vertex", "Vertex mm", "12", { inputMode: "decimal" })}
        </div>
      </fieldset>

      {errors.length > 0 && (
        <ul className="pc-errors" role="alert">
          {errors.map((message) => <li key={message}>{message}</li>)}
        </ul>
      )}

      {out ? (
        <div className="pc-result" aria-live="polite">
          <span className="pc-result-label">Lens power</span>
          <output className="pc-result-value">{out.text}</output>
          <table className="pc-table">
            {out.axes.length > 1 && (
              <thead>
                <tr><th scope="col"><span className="pc-sr">Step</span></th>{out.axes.map((a) => <th key={a} scope="col">{axisLabel(a)}</th>)}</tr>
              </thead>
            )}
            <tbody>
              {out.rows.map((row) => (
                <tr key={row.label} className={`pc-row-${row.kind}`}>
                  <th scope="row">{row.label}</th>
                  {row.values.map((v, i) => (
                    <td key={i}>{change(v)}</td>
                  ))}
                </tr>
              ))}
              <tr className="pc-row-rounded">
                <th scope="row">Rounded</th>
                {out.rounded.map((v, i) => <td key={i}>{dioptres(v)}</td>)}
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        !errors.length && <p className="pc-empty">Enter the over-refraction over the trial lens. A plano over-refraction is “PL”.</p>
      )}

      <details className="pc-rules">
        <summary>How it is worked out</summary>
        <ul>
          <li>The over-refraction, in minus cylinder, as its two meridians.</li>
          <li>Each meridian over 4 D is moved to the eye: F / (1 − d F).</li>
          <li>The trial lens's power is added to both.</li>
          <li>The base curve change: 337.5 × (1/trial − 1/ordered) D. A flatter base curve takes more plus.</li>
          <li>Each meridian is rounded to 0.25 D, an exact half going to the more plus power.</li>
        </ul>
      </details>
    </div>
  );
}
